import styles from './index.module.css'

import { LuTags } from '@react-icons/all-files/lu/LuTags'
import React, { useState } from 'react'

import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import type { Playlist, PlaylistTag } from 'types/playlist'

import PlaylistTagsModal from 'components/common/playlist-tags-modal'
import Button from 'components/ui/button'
import Tag from 'components/ui/tag'

type Props = {
  playlist: Playlist
  tags: Partial<PlaylistTag>[]
  onTagAdd?: (tagId: PlaylistTag['name']) => void
  onTagRemove?: (tagId: PlaylistTag['name']) => void
}

/**
 * PlaylistTagsList component for displaying and managing playlist tags
 *
 * Shows a list of tags associated with a playlist, with support for adding/removing tags.
 * Includes truncation for long lists, color indicators (if enabled), and a modal for tag management.
 * Respects read-only state of playlists.
 *
 * @param props - Component props
 * @param props.playlist - Playlist object containing metadata and permissions
 * @param props.tags - Array of playlist tags to display
 * @param props.onTagAdd - Optional callback function called when adding a tag
 * @param props.onTagRemove - Optional callback function called when removing a tag
 * @returns JSX element containing the tags list and management UI
 */
const PlaylistTagsList = ({
  playlist,
  tags,
  onTagAdd,
  onTagRemove,
}: Props): React.ReactElement<'div'> => {
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const [modalOpen, setModalOpen] = useState(false)
  const [isShowMore, setIsShowMore] = useState(false)

  const toggleTagMenu = () => {
    setModalOpen(!modalOpen)
  }

  const tagList = tags.map((tag: PlaylistTag, index: number) => {
    const removeTag = () => {
      if (onTagRemove) {
        onTagRemove(tag.name)
      }
    }

    return (
      <div className={styles.playlistTagsListItem} key={index}>
        <Tag
          tag={tag.name}
          tagColor={colorIndicatorsEnabled ? tag.color : undefined}
          onRemoveClick={!playlist.read_only ? removeTag : undefined}
        />
      </div>
    )
  })
  const isEmpty = tags.length === 0
  const isTruncated = tagList.length > 6
  return (
    <div className={styles.playlistTagsList}>
      {isEmpty ? (
        <div
          className={styles.playlistTagsListPlaceholder}
          onClick={isEmpty ? toggleTagMenu : undefined}
        >
          Add playlist tags here
        </div>
      ) : (
        <>
          {!isTruncated || isShowMore ? tagList : tagList.slice(0, 5)}
          {isTruncated && (
            <div
              className={styles.playlistTagsListMore}
              onClick={() => {
                setIsShowMore(!isShowMore)
              }}
            >
              {isShowMore ? 'Show less' : 'Show more'}
            </div>
          )}
        </>
      )}
      {onTagAdd && !playlist.read_only && (
        <div className={styles.playlistTagsListButton}>
          <Button
            appearance="icon"
            iconNode={<LuTags size={22} />}
            tooltip="Add playlist tags"
            onClick={toggleTagMenu}
          />
        </div>
      )}
      {modalOpen && (
        <PlaylistTagsModal
          playlistId={playlist.playlist_id}
          onClose={toggleTagMenu}
        />
      )}
    </div>
  )
}

export default PlaylistTagsList
